/* eslint-disable prettier/prettier */
import { AUTH_TOKEN_KEY } from '../constants/app.constant';
import { apiService } from './api.service';
import StorageService from './storage.service';

interface LoginPayload {
	email: string;
	password: string;
}

interface LoginResponse {
	tokens: {
		accessToken: string;
		refreshToken: string;
	};
	user: {
		id: string;
		email: string;
		name?: string;
	};
}

interface RegisterPayload {
	email: string;
	password: string;
	fullname: string;
	username?: string;
	location?: string;
	phonenumber?: string;
	country?: string;
	timezone?: string;
	organizationname?: string;
	designation?: string;
	skills?: string[];
	areasofinterest?: string[];
	growthparameters?: string[];
	languages?: string[];

	yearsofexperience?: string;
	coachingexperience?: string[];
}

interface RegisterResponse {
	tokens: {
		accessToken: string;
		refreshToken: string;
	};
	user: {
		email: string;
		password: string;
		fullname: string;
		username?: string;
		location?: string;
		phonenumber?: string;
		country?: string;
		timezone?: string;
		organizationname?: string;
		designation?: string;
		skills?: string[];
		areasofinterest?: string[];
		growthparameters?: string[];
		languages?: string[];
		yearsofexperience?: string;
		coachingexperience?: string[];
	};
}

interface ChangePasswordResponse {
	success: boolean;
	message: string;
}
interface LogoutResponse {
	success: boolean;
	message: string;
}

export const authService = {
	async loginUser(payload: LoginPayload): Promise<LoginResponse> {
		try {
			const response = await apiService.post<LoginResponse>(
				'/auth/login',
				payload
			);
			return response.data;
		} catch (error: any) {
			console.error('Login error:', error);
			throw new Error(error.response?.data?.message || 'Login failed');
		}
	},

	async registerUser(payload: RegisterPayload): Promise<RegisterResponse> {
		try {
			const response = await apiService.post<RegisterResponse>(
				'/auth/register',
				payload
			);
			return response.data;
		} catch (error: any) {
			console.error('Register error:', error);
			throw new Error(
				error.response?.data?.message || 'Registration failed'
			);
		}
	},

	async changePassword(
		currentPassword: string,
		newPassword: string
	): Promise<ChangePasswordResponse> {
		try {
			const response = await apiService.post<ChangePasswordResponse>(
				'/auth/password/change',
				{ currentPassword, newPassword }
			);

			return response;
		} catch (error: any) {
			console.error('Change password error:', error);
			throw new Error(
				error.response?.data?.message || 'Password change failed'
			);
		}
	},

	async forgotPassword(
		email: string
	): Promise<{ success: boolean; data?: any; message: string }> {
		try {
			const response = await apiService.post<{
				success: boolean;
				data?: any;
				message: string;
			}>('/auth/password/forgotpassword', { email });

			return response.data;
		} catch (error: any) {
			console.error('Forgot password error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to send reset link'
			);
		}
	},

	async socialLogin(
		provider: string
	): Promise<{ success: boolean; data?: any; message: string }> {
		try {
			const response = await apiService.get<{
				success: boolean;
				data?: any;
				message: string;
			}>(`/auth/${provider}`);

			return response.data;
		} catch (error: any) {
			console.error('Forgot password error:', error);
			throw new Error(
				error.response?.data?.message || 'Failed to send reset link'
			);
		}
	},
	async logoutUser(refreshToken: string): Promise<LogoutResponse> {
		try {
			const response = await apiService.post<LogoutResponse>(
				'/auth/logout',
				{ refreshToken }
			);

			return response.data;
		} catch (error: any) {
			console.error('Logout error:', error);
			throw new Error(error.response?.data?.message || 'Logout failed');
		}
	},

	// Get auth token from local storage
	getAuthToken(): string | null {
		return StorageService.get<string | null>(AUTH_TOKEN_KEY, null);
	},

	// Get auth header for API requests
	getAuthHeader(): { Authorization: string } | {} {
		const token = this.getAuthToken();
		return token ? { Authorization: `Bearer ${token}` } : {};
	},
	async resetPassword(
		token: string,
		password: string
	): Promise<{ success: boolean; message: string }> {
		try {
			const response = await apiService.post<{
				success: boolean;
				message: string;
			}>('/auth/password/resetpassword', { token, password });
			console.log('RESET RESPONSE:', response);
			return response;
		} catch (error: any) {
			console.error('Reset password error:', error);
			throw new Error(
				error.response?.data?.message || 'Password reset failed'
			);
		}
	},
};
